import { Global, css } from "@emotion/react";

import { getThemeValue } from "@theme/components";

import { ketchBannerButtonCss } from "@theme/components/GlobalStyle/ketchBannerButtons";
import { Normalize } from "@theme/components/GlobalStyle/Normalize";

export const GlobalStyle = () => (
  <>
    {/* Normalize css to provide better base
        github.com/necolas/normalize.css
      */}
    <Normalize />

    <Global
      styles={(t) => css`
        /* Remove Reach UI styles warnings */
        :root {
          --reach-tabs: 1;
        }

        body {
          position: relative;
        }

        [data-reach-disclosure-button] {
          background: transparent;
          border: none;
          padding: 0;
        }

        [data-reach-disclosure-panel]:focus {
          outline: none;
        }

        /* Fix for multi-line link focus - override keyboard-user :before pseudo-element */
        .keyboard-user p a:focus:before,
        .keyboard-user p a:focus-visible:before {
          display: none !important;
        }

        .keyboard-user p a:focus,
        .keyboard-user p a:focus-visible {
          border: 2px solid ${getThemeValue(t, "colors.goldDark")} !important;
          box-decoration-break: clone !important;
          -webkit-box-decoration-break: clone !important;
          padding: 2px 4px !important;
          margin: -2px -4px !important;
          border-radius: 2px !important;
        }

        a:focus:not(:focus-visible),
        button:focus:not(:focus-visible) {
          outline: none;
        }

        /* remove fade in on gatsby-images with loading="eager" */
        .gatsby-image-wrapper [data-main-image][loading="eager"] {
          transition: opacity 0s linear;
        }

        /* Remove recaptcha badge see: https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed */
        .grecaptcha-badge {
          visibility: hidden;
        }

        /* ThemeUIProvider injects an extra <div> wrapper into the DOM. 
          * We use display: contents to avoid breaking layouts.
          */
        [data-themeui-provider],
        [data-themeui-nested-provider] {
          display: contents;
        }

        /* Hide Coupon Popups */
        html div#__genieContainer,
        html div#cently-portal-popover,
        html div#honeyContainer {
          display: none !important;
          visibility: hidden !important;
        }

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          font-weight: 400;
        }

        /* Force ketch modals to use Graphik Fonts */
        #lanyard_root * {
          font-family: "Graphik";
        }

        /* Some ketch styles can't be applied by their customizations and we need to override */
        ${ketchBannerButtonCss(t)}

        #lanyard_root #banner-description .ketch-text-inherit a {
          font-weight: ${getThemeValue(t, "fontWeights.medium")} !important;
        }

        #lanyard_root #banner-description .ketch-text-inherit a:hover {
          color: ${getThemeValue(t, "colors.textPrimary")} !important;
        }

        /* === React Datepicker Calendar Styles === */
        .react-datepicker-wrapper {
          width: ${getThemeValue(t, "sizes.full")};
        }
        #calendar-portal {
          .react-datepicker {
            background: ${getThemeValue(t, "colors.backgroundLevel2")};
            border: none;
            box-shadow: ${getThemeValue(t, "shadows.alert")};
            font-family: ${getThemeValue(t, "fonts.textSans")};
            color: ${getThemeValue(t, "colors.textPrimary")};
          }
          .react-datepicker-popper {
            z-index: 99999;
          }
          .react-datepicker-popper .react-datepicker__triangle {
            color: ${getThemeValue(t, "colors.backgroundLevel2")};
            fill: ${getThemeValue(t, "colors.backgroundLevel2")};
            stroke: ${getThemeValue(t, "colors.backgroundLevel2")};
          }
          .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle {
            color: ${getThemeValue(t, "colors.backgroundBrandLevel2")};
            fill: ${getThemeValue(t, "colors.backgroundBrandLevel2")};
            stroke: ${getThemeValue(t, "colors.backgroundBrandLevel2")};
          }
          .react-datepicker__header {
            background: ${getThemeValue(t, "colors.backgroundBrandLevel2")};
            color: ${getThemeValue(t, "colors.textInverse")};
            font-family: ${getThemeValue(t, "fonts.textSans")};
            border-bottom: none;
          }
          .react-datepicker__current-month,
          .react-datepicker__day-name {
            color: ${getThemeValue(t, "colors.textInverse")};
            font-weight: ${getThemeValue(t, "fontWeights.bold")};
            font-family: ${getThemeValue(t, "fonts.textSans")};
          }
          .react-datepicker__day {
            color: ${getThemeValue(t, "colors.textPrimary")};
            font-family: ${getThemeValue(t, "fonts.textSans")};
          }
          .react-datepicker__day.react-datepicker__day--outside-month {
            color: ${getThemeValue(t, "colors.interactiveNeutralStrong")};
          }
          .react-datepicker__day.react-datepicker__day--outside-month:hover {
            color: ${getThemeValue(t, "colors.interactiveNeutralStrong")};
          }
          .react-datepicker__day:hover {
            background: ${getThemeValue(t, "colors.backgroundLevel2")};
            color: ${getThemeValue(t, "colors.textPrimary")};
          }
          .react-datepicker__navigation-icon::before {
            border-color: ${getThemeValue(t, "colors.textInverse")};
          }
          .react-datepicker__day--disabled {
            color: ${getThemeValue(t, "colors.interactiveNeutralStrong")};
            cursor: not-allowed;
            &:hover {
              color: ${getThemeValue(t, "colors.interactiveNeutralStrong")};
            }
          }
          .react-datepicker__day--today {
            background: ${getThemeValue(t, "colors.backgroundBrandLevel2")};
            color: ${getThemeValue(t, "colors.textInverse")};
            font-family: ${getThemeValue(t, "fonts.textSans")};
            &:hover {
              background: ${getThemeValue(t, "colors.backgroundBrandLevel2")};
              color: ${getThemeValue(t, "colors.textInverse")};
            }
          }
          .react-datepicker__day--keyboard-selected,
          .react-datepicker__day--selected {
            border: 1px solid ${getThemeValue(t, "colors.textBrandLevel2")};
            background: ${getThemeValue(t, "colors.backgroundLevel2")};
            color: ${getThemeValue(t, "colors.textBrandLevel2")};
            font-weight: ${getThemeValue(t, "fontWeights.bold")};
            &:hover {
              background: ${getThemeValue(t, "colors.backgroundLevel2")};
              color: ${getThemeValue(t, "colors.textBrandLevel2")};
            }
          }
          .react-datepicker__month-select,
          .react-datepicker__year-select {
            color: ${getThemeValue(t, "colors.textPrimary")};
            background: ${getThemeValue(t, "colors.backgroundLevel2")};
            padding: ${getThemeValue(t, "space.s100")};
            margin: ${getThemeValue(t, "space.s200")};
          }
        }

        /* === Command Styles === */
        [data-radix-popper-content-wrapper] {
          z-index: 100 !important; // override default inline z-index of "auto"
        }
      `}
    />
  </>
);
